import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from '../assets/images/arka-saydam-logo.png';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { API_BASE_URL } from '../config';
import {
  normalizeAdminNotificationMeta,
  isRecipeIssueNotification,
  isPricingRiskNotification,
  isCriticalNotification,
  isAcknowledgedUnresolvedNotification
} from '../utils/adminNotificationMeta';

const API = API_BASE_URL;

const getAdminHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AdminSidebar = ({
  mobileOpen = false,
  onCloseMobile = () => { },
  mobileSidebarOpen = false,
  setMobileSidebarOpen = null,
  hiddenPaths = []
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMd = useMediaQuery('(min-width: 768px)');
  const [hoverOpen, setHoverOpen] = useState(false);
  const [userCollapsed, setUserCollapsed] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [paymentUnreadCount, setPaymentUnreadCount] = useState(0);
  const [professionalUnreadCount, setProfessionalUnreadCount] = useState(0);
  const [recipeIssueUnreadCount, setRecipeIssueUnreadCount] = useState(0);
  const [pricingRiskUnreadCount, setPricingRiskUnreadCount] = useState(0);
  const [criticalUnreadCount, setCriticalUnreadCount] = useState(0);
  const avatarRef = useRef(null);

  const effectiveMobileOpen = mobileOpen || mobileSidebarOpen;
  const isExpanded = isMd ? (!userCollapsed || hoverOpen) : true;
  const mdWidth = '18rem';
  const mdCollapsed = '5rem';

  const style = isMd
    ? { width: isExpanded ? mdWidth : mdCollapsed, transform: 'translateX(0)' }
    : { width: '18rem', transform: effectiveMobileOpen ? 'translateX(0)' : 'translateX(-100%)' };

  const closeMobile = () => {
    if (typeof onCloseMobile === 'function') onCloseMobile();
    if (typeof setMobileSidebarOpen === 'function') setMobileSidebarOpen(false);
  };

  const labelClass = (base = '') => {
    const visibility = isExpanded
      ? 'opacity-100 visible translate-x-0 max-w-[220px]'
      : 'opacity-0 invisible -translate-x-4 max-w-0 w-0 overflow-hidden';
    return `whitespace-nowrap transition-all duration-300 ${isMd ? visibility : 'opacity-100 visible max-w-none'} ${base}`;
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/admin/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: getAdminHeaders()
      });
    } catch (err) {
      console.warn('admin logout failed', err);
    }
    localStorage.removeItem('adminToken');
    navigate('/');
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        let res = await fetch(`${API}/auth/admin/me`, { credentials: 'include', headers: getAdminHeaders() });
        if (!res.ok) {
          res = await fetch(`${API}/auth/me`, { credentials: 'include' });
        }
        if (!mounted || !res.ok) return;
        const j = await res.json().catch(() => null);
        if (!j) return;

        let avatar = j.avatar || null;
        let userObj = j;

        if (!avatar) {
          try {
            const pRes = await fetch(`${API}/api/admin/user/${j.id}/profile`, {
              credentials: 'include',
              cache: 'no-store',
              headers: getAdminHeaders()
            });
            if (pRes.ok) {
              const pj = await pRes.json().catch(() => null);
              const profile = pj?.profile || null;
              const survey = pj?.survey || null;
              const intake = profile?.intakeSurvey || profile?.answers || survey?.answers || profile || {};
              avatar = intake?.avatar || intake?.answers?.avatar || null;
            }
          } catch (e) {
            // ignore avatar enrichment failure
          }
        }

        if (avatar) userObj = { ...j, avatar };
        setAdminUser(userObj);
      } catch (e) {
        // ignore
      }
    })();

    const onProfileUpdated = (ev) => {
      try {
        const d = ev?.detail;
        if (!d?.userId) return;
        setAdminUser((prev) => {
          if (!prev) return { id: d.userId, avatar: d.avatar || null, name: d.name || null, email: d.email || null };
          if (prev.id !== d.userId) return prev;
          return { ...prev, avatar: d.avatar || prev.avatar, name: d.name || prev.name, email: d.email || prev.email };
        });
      } catch (e) {
        // ignore
      }
    };

    window.addEventListener?.('adminProfileUpdated', onProfileUpdated);
    return () => {
      mounted = false;
      window.removeEventListener?.('adminProfileUpdated', onProfileUpdated);
    };
  }, []);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
        const [notificationsRes, paymentsRes, proSubsRes] = await Promise.all([
          fetch(`${API}/api/notifications`, {
            credentials: 'include',
            headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {}
          }),
          fetch(`${API}/api/admin/payment-intents?status=all`, {
            credentials: 'include',
            headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {}
          }),
          fetch(`${API}/api/admin/pro-subscriptions`, {
            credentials: 'include',
            headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {}
          })
        ]);
        if (notificationsRes.ok) {
          const data = await notificationsRes.json();
          const allNotifications = (Array.isArray(data.notifications) ? data.notifications : []).map(normalizeAdminNotificationMeta);
          const unreadNotifications = allNotifications.filter((n) => !n.isRead);
          const acknowledgedUnresolvedNotifications = allNotifications.filter(isAcknowledgedUnresolvedNotification);
          const unresolvedAwareNotifications = [
            ...unreadNotifications,
            ...acknowledgedUnresolvedNotifications.filter((ack) => !unreadNotifications.some((u) => u.id === ack.id))
          ];

          const unread = unresolvedAwareNotifications.length || 0;
          setUnreadCount(unread);
          const recipeIssueUnread = unresolvedAwareNotifications.filter(isRecipeIssueNotification).length;
          setRecipeIssueUnreadCount(recipeIssueUnread);
          const pricingRiskUnread = unresolvedAwareNotifications.filter(isPricingRiskNotification).length;
          setPricingRiskUnreadCount(pricingRiskUnread);
          const criticalUnread = unresolvedAwareNotifications.filter(isCriticalNotification).length;
          setCriticalUnreadCount(criticalUnread);
        }
        if (paymentsRes.ok) {
          const data = await paymentsRes.json().catch(() => ({}));
          const paymentUnread = Number(data.summary?.receiptUploaded || 0) + Number(data.summary?.manualReview || 0);
          setPaymentUnreadCount(paymentUnread);
        }
        if (proSubsRes.ok) {
          const data = await proSubsRes.json().catch(() => ({}));
          const items = Array.isArray(data.items) ? data.items : [];
          const awaiting = items.filter((item) => ['trialing', 'inactive', 'past_due', 'suspended'].includes(String(item?.status || '').toLowerCase())).length;
          setProfessionalUnreadCount(awaiting);
        }
      } catch (err) {
        console.warn('AdminSidebar notification fetch failed', err);
      }
    };

    fetchUnread();
    window.addEventListener('refreshNotifications', fetchUnread);
    window.addEventListener('realtime:notifications-updated', fetchUnread);
    window.addEventListener('realtime:payment-updated', fetchUnread);
    window.addEventListener('realtime:force-refetch', fetchUnread);
    const interval = setInterval(fetchUnread, 300000);
    return () => {
      window.removeEventListener('refreshNotifications', fetchUnread);
      window.removeEventListener('realtime:notifications-updated', fetchUnread);
      window.removeEventListener('realtime:payment-updated', fetchUnread);
      window.removeEventListener('realtime:force-refetch', fetchUnread);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (isMd && !e.target.closest('aside')) {
        setUserCollapsed(true);
      }
    };
    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, [isMd]);

  useEffect(() => {
    const width = isMd ? (isExpanded ? mdWidth : mdCollapsed) : '0rem';
    document.documentElement.style.setProperty('--admin-sidebar-width', width);
    return () => {
      document.documentElement.style.setProperty('--admin-sidebar-width', '18rem');
    };
  }, [isMd, isExpanded]);

  const isActive = (path) => {
    if (!location?.pathname) return false;
    if (path === '/admin') return location.pathname === '/admin' || location.pathname === '/admin/';
    return location.pathname.startsWith(path);
  };

  const gravatarUrl = adminUser?.email
    ? `https://www.gravatar.com/avatar/${encodeURIComponent(adminUser.email)}?d=mp&s=200`
    : 'https://www.gravatar.com/avatar/?d=mp&s=200';
  const avatarSrc = adminUser?.avatar || gravatarUrl;
  const displayName = adminUser?.name || adminUser?.email || 'Admin User';
  const displayEmail = adminUser?.email || 'admin@yemekapp.com';

  const NAV_ITEMS = [
    { path: '/admin', icon: 'dashboard', label: 'Genel Bakış' },
    { path: '/admin/product-analytics', icon: 'analytics', label: 'Ürün Analizi' },
    { path: '/admin/behavior-templates', icon: 'chat', label: 'Mesaj Şablonları' },
    { path: '/admin/behavior-insights', icon: 'lightbulb', label: 'Davranış İçgörüleri' },
    { path: '/admin/behavior-metrics', icon: 'monitoring', label: 'Davranış Ölçümleri' },
    { path: '/admin/users', icon: 'group', label: 'Kullanıcılar' },
    { path: '/admin/subscriptions', icon: 'workspace_premium', label: 'Abonelikler' },
    { path: '/admin/surveys', icon: 'pie_chart', label: 'Anketler' },
    { path: '/admin/pricing', icon: 'monitoring', label: 'Fiyat İstihbaratı' },
    { path: '/admin/recipe-feedback', icon: 'report_problem', label: 'Tarif Sorun Merkezi' },
    { path: '/admin/config', icon: 'settings', label: 'Sistem Ayarları' },
    { path: '/admin/alerjanlar', icon: 'inventory', label: 'Alerjan Takip' },
    { path: '/admin/veri-havuzu', icon: 'database', label: 'Veri Havuzu' },
    { path: '/admin/advertisements', icon: 'campaign', label: 'Reklamlar' },
    { path: '/admin/category-images', icon: 'image', label: 'Kategori Görselleri' },
    { path: '/admin/recipes-manage', icon: 'restaurant_menu', label: 'Tarifleri Yönet' },
    { path: '/admin/onboarding', icon: 'auto_awesome', label: 'Onboarding Yönetimi' },
    { path: '/admin/onboarding/starter-menu', icon: 'calendar_month', label: 'Başlangıç Menüsü' },
    { path: '/admin/blog', icon: 'article', label: 'Blog Yönetimi' },
    { path: '/admin/faqs', icon: 'quiz', label: 'FAQ Yönetimi' },
    { path: '/admin/kullanim-kilavuzu', icon: 'menu_book', label: 'Kullanım Kılavuzu' },
    { path: '/admin/policies', icon: 'policy', label: 'Politikalar' }
  ];

  const hiddenPathSet = new Set(Array.isArray(hiddenPaths) ? hiddenPaths : []);
  const visibleNavItems = NAV_ITEMS.filter((item) => !hiddenPathSet.has(item.path));

  const renderNavLinks = (onItemClick) => (
    <>
      <p className={`mb-2 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500 ${labelClass()}`}>Menü</p>
      {visibleNavItems.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          onClick={onItemClick}
          className={`relative flex items-center rounded-lg py-3 transition-colors ${isExpanded ? 'gap-3 px-4' : 'justify-center px-2'
            } ${isActive(item.path) ? 'bg-primary text-white' : 'text-slate-400 hover:bg-white/5 hover:text-white'}`}
        >
          <span className="material-symbols-outlined shrink-0 text-[22px]">{item.icon}</span>
          <span className={labelClass('text-sm font-medium')}>{item.label}</span>
          {item.path === '/admin/subscriptions' && (paymentUnreadCount + professionalUnreadCount) > 0 && (
            <span className="absolute right-4 top-1/2 flex h-5 min-w-[20px] -translate-y-1/2 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-black text-slate-950 shadow-lg">
              {(paymentUnreadCount + professionalUnreadCount) > 9 ? '9+' : (paymentUnreadCount + professionalUnreadCount)}
            </span>
          )}
          {item.path === '/admin/recipe-feedback' && recipeIssueUnreadCount > 0 && (
            <span className="absolute right-4 top-1/2 flex h-5 min-w-[20px] -translate-y-1/2 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-black text-white shadow-lg">
              {recipeIssueUnreadCount > 9 ? '9+' : recipeIssueUnreadCount}
            </span>
          )}
          {item.path === '/admin/pricing' && pricingRiskUnreadCount > 0 && (
            <span className={`absolute right-4 top-1/2 flex h-5 min-w-[20px] -translate-y-1/2 items-center justify-center px-1 text-[10px] font-black text-white shadow-lg ${
              criticalUnreadCount > 0 ? 'rounded-full bg-red-600 animate-pulse' : 'rounded-full bg-amber-500'
            }`}>
              {pricingRiskUnreadCount > 9 ? '9+' : pricingRiskUnreadCount}
            </span>
          )}
        </Link>
      ))}

      <Link
        to="/admin/notifications"
        onClick={onItemClick}
        className={`relative flex items-center rounded-lg py-3 transition-colors ${isExpanded ? 'gap-3 px-4' : 'justify-center px-2'
          } ${isActive('/admin/notifications') ? 'bg-primary text-white' : 'text-slate-400 hover:bg-white/5 hover:text-white'}`}
      >
        <span className="material-symbols-outlined shrink-0 text-[22px]">notifications</span>
        <span className={labelClass('text-sm font-medium')}>Bildirimler</span>
        {unreadCount > 0 && (
          <span className={`absolute right-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-[10px] font-black text-white shadow-lg ${
            criticalUnreadCount > 0 ? 'bg-red-600 animate-pulse' : 'bg-amber-500'
          }`}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Link>
    </>
  );

  return (
    <>
      <aside
        onMouseEnter={() => isMd && setHoverOpen(true)}
        onMouseLeave={() => isMd && setHoverOpen(false)}
        onClick={() => isMd && setUserCollapsed(false)}
        style={style}
        className="fixed left-0 top-0 z-40 hidden h-full shrink-0 flex-col justify-between overflow-x-hidden border-r border-slate-800 bg-[#101922] shadow-2xl transition-all duration-300 md:flex"
      >
        <div className="flex h-full flex-col">
          <div className={`flex items-center border-b border-slate-800 ${isExpanded ? 'gap-3 p-5' : 'justify-center p-4'}`}>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-white/10">
              <img src={Logo} alt="BBTV Logo" className="h-8 w-8 object-contain" />
            </div>
            <div className={labelClass()}>
              <h2 className="text-lg font-bold leading-none text-white">BBTV</h2>
              <p className="mt-1 text-xs font-medium text-slate-400">Yönetici Paneli</p>
            </div>
          </div>

          <nav className={`scrollbar-hide flex-1 space-y-1 overflow-y-auto py-4 ${isExpanded ? 'px-4' : 'px-2'}`}>
            {renderNavLinks()}
          </nav>

          <div className={`border-t border-slate-800 ${isExpanded ? 'p-4' : 'p-3'}`}>
            <div className={`flex items-center ${isExpanded ? 'gap-3' : 'justify-center gap-2'}`}>
              <div className="relative shrink-0">
                <img
                  ref={avatarRef}
                  src={avatarSrc}
                  alt={displayName}
                  className="h-10 w-10 rounded-full border-2 border-slate-700 object-cover"
                  onError={(e) => {
                    if (e.currentTarget) {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = gravatarUrl;
                    }
                  }}
                />
                {unreadCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-600 text-[10px] font-black text-white ring-1 ring-[#101922]">
                    !
                  </span>
                )}
              </div>
              <div className={`flex flex-col ${labelClass()}`}>
                <p className="text-sm font-medium text-white">{displayName}</p>
                <p className="text-xs text-slate-500">{displayEmail}</p>
              </div>
              <button onClick={handleLogout} className={`${isExpanded ? 'ml-auto' : ''} text-slate-400 hover:text-white`}>
                <span className="material-symbols-outlined text-xl">logout</span>
              </button>
            </div>
          </div>
        </div>
      </aside>

      {effectiveMobileOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div className="fixed inset-0 z-40 bg-black/40" onClick={closeMobile} />
          <aside className="z-50 flex h-full w-72 shrink-0 flex-col justify-between border-r border-slate-800 bg-[#101922] shadow-2xl">
            <div className="flex h-full flex-col">
              <div className="flex items-center gap-3 border-b border-slate-800 p-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-white/10">
                  <img src={Logo} alt="BBTV Logo" className="h-8 w-8 object-contain" />
                </div>
                <div>
                  <h2 className="text-lg font-bold leading-none text-white">BBTV</h2>
                  <p className="mt-1 text-xs font-medium text-slate-400">Yönetici Paneli</p>
                </div>
              </div>

              <nav className="scrollbar-hide flex-1 space-y-1 overflow-y-auto px-4 py-4">
                {renderNavLinks(closeMobile)}
              </nav>

              <div className="border-t border-slate-800 p-4">
                <div className="flex items-center gap-3">
                  <div className="shrink-0">
                    <img
                      src={avatarSrc}
                      alt={displayName}
                      className="h-10 w-10 rounded-full border-2 border-slate-700 object-cover"
                      onError={(e) => {
                        if (e.currentTarget) {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = gravatarUrl;
                        }
                      }}
                    />
                  </div>
                  <div className="flex flex-col">
                    <p className="text-sm font-medium text-white">{displayName}</p>
                    <p className="text-xs text-slate-500">{displayEmail}</p>
                  </div>
                  <button onClick={() => { handleLogout(); closeMobile(); }} className="ml-auto text-slate-400 hover:text-white">
                    <span className="material-symbols-outlined text-xl">logout</span>
                  </button>
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}
    </>
  );
};

export default AdminSidebar;
